import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerTitle,
} from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { compareWorkerAccounts } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useCampaignMutations from "../../../../datahooks/useCampaignMutations";
import AccountCard from "./AccountCard";

const ACCOUNTS_LIMIT = 15;

interface ManageAccountsModalProps {
  isOpen: boolean;
  actionType: "add" | "remove";
  onClose: () => void;
}

export default function ManageAccountsModal({
  isOpen,
  actionType,
  onClose,
}: ManageAccountsModalProps) {
  const { id: campaignId } = useCampaignContext();
  const isDesktop = useTwBreakpoint("lg");
  const [stepIndex, setStepIndex] = useState(0);
  const [accountId, setAccountId] = useState<string>(null);
  const [searchText, setSearchText] = useState("");

  const {
    addAccountToCampaign,
    isAddingAccountToCampaign,
    removeAccountFromCampaign,
    isRemovingAccountFromCampaign,
  } = useCampaignMutations();

  const isAddingAccount = actionType === "add";

  const { accounts: workspaceAccounts } = useSelectedWorkspaceContext();
  const { owners } = useCampaignContext();

  const accounts = workspaceAccounts
    .filter(({ id }) => {
      if (isAddingAccount) {
        return !owners.includes(id);
      }

      return owners.includes(id);
    })
    .sort(compareWorkerAccounts);

  const filteredAccounts = accounts.filter(({ full_name }) =>
    full_name.toLowerCase().includes(searchText.toLowerCase()),
  );

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Close = isDesktop ? DialogClose : DrawerClose;

  function resetState() {
    setStepIndex(0);
    setAccountId(null);
    setSearchText("");
  }

  function onNext() {
    if (isAddingAccount) {
      addAccountToCampaign({ campaignId, accountId }).then(() => {
        onClose();
        setTimeout(() => resetState(), 300);
      });
    } else {
      setStepIndex(1);
    }
  }

  function removeAccount() {
    removeAccountFromCampaign({ campaignId, accountId }).then(() => {
      onClose();
      setTimeout(() => resetState(), 300);
    });
  }

  let title: string;
  let description: string;
  if (stepIndex === 0) {
    title = isAddingAccount
      ? "Add account to campaign"
      : "Remove account from campaign";

    description = isAddingAccount
      ? "The campaign will automatically assigning available targets to them"
      : "In-progress targets will be cancelled. Remaining targets will be reassigned to other accounts";
  } else {
    title = "Remove account?";
    description = "Are you sure you want to remove the selected account?";
  }

  return (
    <Component
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      onAfterClose={resetState}
    >
      <Content className={stepIndex === 0 && "max-lg:pb-12"}>
        <Title>{title}</Title>
        <Description>{description}</Description>

        {stepIndex === 0 ? (
          <>
            <RenderIf condition={accounts.length > ACCOUNTS_LIMIT}>
              <div className="-mb-1 mt-4 px-2 ">
                <SearchInput
                  placeholder="Search accounts"
                  onChange={(e) => setSearchText(e.target.value)}
                  value={searchText}
                  onClear={() => setSearchText("")}
                />
              </div>
            </RenderIf>

            <div className="my-4 flex max-h-[min(378px,60dvh)] flex-col gap-y-3 overflow-y-auto px-2 scrollbar-thin">
              {filteredAccounts.map((account) => (
                <AccountCard
                  key={account.id}
                  isSelected={account.id === accountId}
                  onSelect={(id) => setAccountId(id)}
                  account={account}
                />
              ))}
            </div>

            <Footer>
              <Close asChild>
                <Button
                  variant="secondary-black"
                  disabled={isAddingAccountToCampaign}
                >
                  Cancel
                </Button>
              </Close>

              <Button
                variant={isAddingAccount ? "default-black" : "outline-danger"}
                disabled={!accountId}
                onClick={onNext}
                isLoading={isAddingAccountToCampaign}
              >
                {isAddingAccount ? "Add account" : "Remove"}
              </Button>
            </Footer>
          </>
        ) : (
          <div className="mt-8 flex flex-col gap-y-2 lg:flex-row-reverse lg:gap-x-4">
            <Button
              onClick={removeAccount}
              isLoading={isRemovingAccountFromCampaign}
              className="flex-1"
              variant="outline-danger"
            >
              Remove
            </Button>
            <Button
              variant="secondary-black"
              onClick={() => setStepIndex(0)}
              className="flex-1"
              disabled={isRemovingAccountFromCampaign}
            >
              Cancel
            </Button>
          </div>
        )}
      </Content>
    </Component>
  );
}
